import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { ConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { FileCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { SalesDraftOrder } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { DraftFormValues } from './use-draft-form.ts';

export type UseConfirmSubmitOptions = {
    draft: SalesDraftOrder | undefined;
};

export type UseConfirmSubmitHook = {
    error?: { data?: { displayMessage?: string } | null };
    isPending: boolean;
    submit: (values: DraftFormValues) => void;
};

export function useConfirmSubmit({ draft }: UseConfirmSubmitOptions): UseConfirmSubmitHook {
    const { t } = useTranslation('sales-draft');
    const { toast } = useToast();
    const { canReadQuoteOrderFromGrid } = useAppConfig();
    const sendEvent = useAnalytics();
    const navigate = useNavigate();

    const {
        mutate: submit,
        isPending,
        error,
    } = api.platoUser.command.salesDraft.draft.submitWithHeader.useMutation({
        onError: () => {
            toast({
                title: t('sales_draft__submit_error_toast__title'),
                description: t('sales_draft__submit_error_toast__description'),
                icon: 'warning',
            });
        },
        onMutate: (values) => {
            if (!draft) {
                return;
            }
            sendEvent('quote_order_submitted', {
                draft_id: values.draft_order_id,
                quote_order_type: values.transaction_type,
                number_of_items: draft.line_items.length,
                total_sum: draft.total_amount,
                customer_id: values.customer_id,
            });
        },
        onSuccess: ({ draft_order: draft }, values) => {
            toast({
                title:
                    values.transaction_type === 'ORDER'
                        ? t('sales_draft__ORDER__submit_success_toast__title')
                        : t('sales_draft__QUOTE__submit_success_toast__title'),
                description:
                    values.transaction_type === 'ORDER'
                        ? t('sales_draft__ORDER__submit_success_toast__description')
                        : t('sales_draft__QUOTE__submit_success_toast__description'),
                icon: 'success',
            });

            if (draft.sales_order_id && canReadQuoteOrderFromGrid) {
                void navigate(`/${values.transaction_type.toLowerCase()}s/${draft.sales_order_id}`, {
                    replace: true,
                });
            } else {
                void navigate('/drafts', { replace: true });
            }
        },
    });

    function confirm(values: DraftFormValues): void {
        if (!draft) {
            return;
        }
        void DialogStateManager.show(ConfirmationDialog, {
            focus: 'submit',
            icon: FileCheck,
            title:
                values.transaction_type === 'ORDER'
                    ? t('sales_draft__ORDER__submit_confirmation_dialog__title')
                    : t('sales_draft__QUOTE__submit_confirmation_dialog__title'),
            description:
                values.transaction_type === 'ORDER'
                    ? t('sales_draft__ORDER__submit_confirmation_dialog__description')
                    : t('sales_draft__QUOTE__submit_confirmation_dialog__description'),
            accept: {
                text: t('sales_draft__submit_confirmation_dialog__accept'),
                action: () => {
                    submit(values);
                },
            },
            reject: {
                text: t('sales_draft__submit_confirmation_dialog__reject'),
            },
        });
    }

    return {
        error: error ?? undefined,
        isPending,
        submit: confirm,
    };
}
